import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2, Upload } from 'lucide-react';
import { PageHeader } from '@/components/ui/Card.js';
import { Button } from '@/components/ui/Button.js';
import { Select } from '@/components/ui/Field.js';
import { ConfirmDialog, Modal } from '@/components/ui/Modal.js';
import { EmptyState } from '@/components/ui/DataTable.js';
import { useToast } from '@/components/ui/Toast.js';
import { TranslatableInput } from '@/components/form/TranslatableInput.js';
import { MediaThumb } from '@/components/media/MediaPicker.js';
import { fileSize, dateTime } from '@/lib/format.js';
import { queryKeys } from '@/lib/query-keys.js';
import { ApiError } from '@/lib/api-error.js';
import { useAuth } from '@/features/auth/auth-context.js';
import { collectionLabel, mediaApi, type MediaItem, type MediaUsage } from './media-api.js';

const KIND_OPTIONS = [
  { value: 'image', label: 'Rasmlar' },
  { value: 'file', label: 'Hujjatlar' },
];

/**
 * Media kutubxona (B6 DoD).
 *
 * Uchta talab: ko'p fayl yuklash, «qayerda ishlatilgan» va o'chirishda
 * tasdiq. Oxirgisi ayniqsa muhim — o'chirilgan rasm o'nlab sahifada
 * buzilgan tasvirga aylanadi.
 */
export function MediaPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);

  const [kind, setKind] = useState<'image' | 'file' | ''>('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<MediaItem | null>(null);

  const params = { page, limit: 24, kind: kind || undefined, search: search || undefined };
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.media.list(params),
    queryFn: () => mediaApi.list(params),
  });

  const upload = useMutation({
    mutationFn: (files: File[]) => mediaApi.upload(files, null),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.media.all });
      toast.success(`${result.data.length} ta fayl yuklandi`);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <>
      <PageHeader
        title="Media kutubxona"
        description="Rasmlar va hujjatlar. Bir vaqtda bir nechta fayl yuklash mumkin."
        action={
          <Button
            icon={<Upload className="size-4" aria-hidden />}
            loading={upload.isPending}
            onClick={() => inputRef.current?.click()}
          >
            Fayl yuklash
          </Button>
        }
      />

      <input
        ref={inputRef}
        type="file"
        multiple
        hidden
        accept="image/jpeg,image/png,image/webp,application/pdf"
        onChange={(event) => {
          const files = Array.from(event.target.files ?? []);
          if (files.length > 0) upload.mutate(files);
          event.target.value = '';
        }}
      />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="w-48">
          <Select
            label="Turi"
            placeholder="Hammasi"
            options={KIND_OPTIONS}
            value={kind}
            onChange={(event) => {
              setKind(event.target.value as 'image' | 'file' | '');
              setPage(1);
            }}
          />
        </div>

        <div className="min-w-56 flex-1">
          <label htmlFor="media-search" className="mb-1.5 block text-sm font-medium text-n-800">
            Qidiruv
          </label>
          <input
            id="media-search"
            type="search"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            placeholder="Fayl nomi"
            className="h-10 w-full border border-n-200 px-3 text-sm"
          />
        </div>
      </div>

      {isLoading && <p className="py-8 text-center text-sm text-n-600">Yuklanmoqda…</p>}

      {!isLoading && (data?.data.length ?? 0) === 0 && (
        <div className=" border border-n-200 bg-white">
          <EmptyState
            title="Kutubxona bo‘sh"
            hint="«Fayl yuklash» tugmasi orqali rasm yoki PDF qo‘shing. Bir vaqtda 20 tagacha fayl tanlash mumkin."
          />
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6">
        {data?.data.map((item) => (
          <button
            key={item._id}
            type="button"
            onClick={() => setSelected(item)}
            className="overflow-hidden border border-n-200 bg-white text-left transition-colors hover:border-steel-500"
          >
            <MediaThumb item={item} />
            <div className="p-2">
              <p className="truncate text-xs font-medium text-n-800">{item.originalName}</p>
              <p className="text-[11px] text-n-400">
                {fileSize(item.size)}
                {item.width && item.height && ` · ${item.width}×${item.height}`}
              </p>
            </div>
          </button>
        ))}
      </div>

      {data && data.meta.totalPages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-3 text-sm">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            Oldingi
          </Button>
          <span className="tabular text-n-600">
            {data.meta.page} / {data.meta.totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= data.meta.totalPages}
            onClick={() => setPage(page + 1)}
          >
            Keyingi
          </Button>
        </div>
      )}

      {selected && (
        <MediaDetails
          item={selected}
          canDelete={can('superadmin', 'admin')}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
}

function MediaDetails({
  item,
  canDelete,
  onClose,
}: {
  item: MediaItem;
  canDelete: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [alt, setAlt] = useState(item.alt);
  const [confirmForce, setConfirmForce] = useState<MediaUsage[] | null>(null);
  const [confirmSimple, setConfirmSimple] = useState(false);

  const { data: usages } = useQuery({
    queryKey: queryKeys.media.usages(item._id),
    queryFn: () => mediaApi.usages(item._id),
  });

  const save = useMutation({
    mutationFn: () => mediaApi.update(item._id, { alt }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.media.all });
      toast.success('Saqlandi');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const remove = useMutation({
    mutationFn: (force: boolean) => mediaApi.remove(item._id, force),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.media.all });
      toast.success('Fayl o‘chirildi');
      onClose();
    },
    onError: (error: Error) => {
      // Server ishlatilayotgan faylni oddiy so'rovda o'chirmaydi — u holda
      // qayerda ishlatilganini ko'rsatib, ataylab tasdiqlashni so'raymiz.
      if (error instanceof ApiError && error.code === 'MEDIA_IN_USE') {
        setConfirmSimple(false);
        setConfirmForce(usages?.data ?? []);
        return;
      }
      toast.error(error.message);
    },
  });

  const usageList = usages?.data ?? [];

  return (
    <>
      <Modal
        open
        onClose={onClose}
        title={item.originalName}
        description={`${fileSize(item.size)} · ${dateTime(item.createdAt)}`}
        size="lg"
        footer={
          <>
            {canDelete && (
              <Button
                variant="danger"
                icon={<Trash2 className="size-4" aria-hidden />}
                onClick={() => setConfirmSimple(true)}
              >
                O‘chirish
              </Button>
            )}
            <Button variant="outline" onClick={onClose}>
              Yopish
            </Button>
            <Button loading={save.isPending} onClick={() => save.mutate()}>
              Saqlash
            </Button>
          </>
        }
      >
        <div className="grid gap-5 sm:grid-cols-2">
          <MediaThumb item={item} className=" border border-n-200" />

          <div className="space-y-4">
            <TranslatableInput
              label="Alt matni"
              value={alt}
              onChange={setAlt}
              hint="Rasm ochilmasa ko‘rsatiladi va qidiruv tizimlari uchun muhim"
            />

            <div>
              <h3 className="mb-1.5 text-sm font-medium text-n-800">Qayerda ishlatilgan</h3>
              {usageList.length === 0 ? (
                <p className="text-sm text-n-600">
                  Hech qayerda ishlatilmagan — bemalol o‘chirish mumkin.
                </p>
              ) : (
                <ul className="space-y-1 text-sm text-n-800">
                  {usageList.map((usage) => (
                    <li key={`${usage.collectionName}-${usage.docId}-${usage.field}`}>
                      <span className="font-medium">{collectionLabel(usage.collectionName)}</span>
                      <span className="text-n-400"> · {usage.field}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={confirmSimple}
        title="Faylni o‘chirish"
        message={
          <>
            <strong>{item.originalName}</strong> butunlay o‘chiriladi. Bu amalni qaytarib bo‘lmaydi.
          </>
        }
        confirmLabel="O‘chirish"
        danger
        loading={remove.isPending}
        onConfirm={() => remove.mutate(false)}
        onCancel={() => setConfirmSimple(false)}
      />

      <ConfirmDialog
        open={confirmForce !== null}
        title="Fayl ishlatilmoqda"
        message={
          <div className="space-y-2">
            <p>
              Bu fayl <strong>{confirmForce?.length ?? usageList.length}</strong> joyda
              ishlatilmoqda. O‘chirilsa, o‘sha sahifalarda rasm ko‘rinmay qoladi.
            </p>
            <ul className="list-inside list-disc text-n-600">
              {(confirmForce ?? []).slice(0, 8).map((usage) => (
                <li key={`${usage.collectionName}-${usage.docId}-${usage.field}`}>
                  {collectionLabel(usage.collectionName)} · {usage.field}
                </li>
              ))}
            </ul>
          </div>
        }
        confirmLabel="Baribir o‘chirish"
        danger
        loading={remove.isPending}
        onConfirm={() => remove.mutate(true)}
        onCancel={() => setConfirmForce(null)}
      />
    </>
  );
}
